import { css, Mode, NumericStepper, styled, useModeContext } from 'canoe';
import React from 'react';

import { NumericStepperProps } from 'canoe/components/NumericStepper';

import { ValueBoxConsumer } from '../ValueBox';

const StyledStepper = styled(NumericStepper)<{
  isInGroup?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  isExpandable?: boolean;
  isExpanded?: boolean;
  hasBoxLabel?: boolean;
  hasError?: boolean;
  responsiveWidth?: boolean;
  mode?: Mode;
}>`
  ${({
    theme,
    isInGroup,
    isFirst,
    isLast,
    isExpandable,
    isExpanded,
    hasBoxLabel,
    hasError,
    mode,
  }) => css`
    height: 32px;
    box-shadow: none;

    ${
      mode === 'Dark'
        ? css`
            border: 1px solid ${theme.colors.OnyxGrayL10};
          `
        : css`
            border: 1px solid ${theme.colors.GlitterGray};

            &:hover,
            &:focus {
              border-color: ${theme.colors.GlitterGrayD20};
            }
          `
    }
    &:hover,
    &:focus {
      box-shadow: none;
    }

    ${
      hasBoxLabel &&
      css`
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
      `
    }

    ${
      isInGroup &&
      css`
        ${groupStyles({ isFirst, isLast, hasBoxLabel })};
      `
    }

    ${
      isExpandable &&
      css`
        ${expandableStyles({ isExpanded, hasBoxLabel })};
      `
    }

    ${
      hasError &&
      css`
        outline: 1px solid ${theme.colors.Danger};
      `
    }
  `}
`;

const groupStyles = ({
  isFirst,
  isLast,
  hasBoxLabel,
}: {
  isFirst?: boolean;
  isLast?: boolean;
  hasBoxLabel?: boolean;
}) => css`
  ${isFirst &&
  css`
    border-bottom-right-radius: 0;
  `};
  ${isLast &&
  css`
    border-top-right-radius: 0;
  `};

  &:not(:last-child) {
    // if a stepper is nested between a label and a toggle, we need to override the above logic
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-right-width: 0;
  }

  ${!isFirst &&
  !isLast &&
  css`
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  `};

  ${!hasBoxLabel &&
  css`
    ${
      isFirst &&
      css`
        border-bottom-left-radius: 0;
      `
    }
    ${
      isLast &&
      css`
        border-top-left-radius: 0;
      `
    }

    ${
      !isFirst &&
      !isLast &&
      css`
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
      `
    }
  `}
`;

const expandableStyles = ({
  isExpanded,
  hasBoxLabel,
}: {
  isExpanded?: boolean;
  hasBoxLabel?: boolean;
}) => css`
  ${isExpanded &&
  css`
    border-bottom-right-radius: 0;

    ${!hasBoxLabel &&
    css`
      border-bottom-left-radius: 0;
    `}
  `}
`;

export const ValueStepper = React.forwardRef<HTMLInputElement, NumericStepperProps>(
  ({ ...rest }: NumericStepperProps, ref) => {
    const { mode } = useModeContext();

    return (
      <ValueBoxConsumer>
        {(contextValues) => <StyledStepper ref={ref} mode={mode} {...contextValues} {...rest} />}
      </ValueBoxConsumer>
    );
  },
);
